import { AUTH_EVENTS } from "@/constants/amplitude.constants";
import { useSignupPageSetup } from "@/hooks/useSignupPageSetup";
import { GTM_ID, RECAPTCHA_TOKEN } from "@/shared/constants/env/externalService.constants";
import { GOOGLE_EVENTS } from "@/constants/googleTagEvents.constants";
import { SIGNIN_TYPE } from "@/shared/constants/signInConstants";
import { SIGNUP_TYPES } from "@/shared/constants/signUpConstants";
import SignupController from "@/shared/controllers/SignUp";
import { TOAST_TYPES } from "@/shared/data/enums/toast";
import { SignupCopiesType } from "@/shared/data/navbar.data";
import Amplitude from "@/shared/services/amplitude.service";
import { sendGoogleTagEvent } from "@/shared/services/googleAnalytics.service";
import useSignInStore from "@/shared/store/useSignInStore";
import useSignUpStore from "@/shared/store/useSignUpStore";
import { SIGNUP_TYPE } from "@/shared/types/enums/signup";
import { CreatorSignupType, OTPVerificationResponse } from "@/shared/types/signup.types";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { isBrowser } from "@/shared/utils/dom";
import { emptyObject } from "@/shared/utils/empty";
import { isUceCountryCode } from "@/shared/utils/route.util";
import {
  otpAuthenticate,
  otpAuthenticateOnSignUp,
  spayeeAuthenticate,
  spayeeAuthenticateSignUp,
} from "@/shared/utils/spayee.utils";
import { clearFromLocal } from "@/shared/utils/storage.util";
import { getPathName, getQueryObjFromURL, isRunningOnRootHost } from "@/shared/utils/url.util";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import { Div } from "@graphy/ui";
import * as EmailValidator from "email-validator";
import generator from "generate-password";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { parseCookies } from "nookies";
import { useCallback, useState } from "react";
import OtpInput from "react-otp-input";
import Loader from "../atoms/Loader";
import ToastAlert from "./ToastAlert";

export type OtpInputProps = {
  creatorDomain: string;
  signupCopies: SignupCopiesType;
  OTPActionType: "signup" | "signin";
  resendOtp?: number;
  onResend?: () => void;
};

interface otp {
  enteredOtp: string;
  errorMsg: string;
}

export const OtpInputComponent = ({
  creatorDomain,
  signupCopies,
  OTPActionType,
  resendOtp,
  onResend,
}: OtpInputProps) => {
  const [otp, setOtp] = useState<otp>({ enteredOtp: "", errorMsg: "" });

  const router = useRouter();

  const path = usePathname();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAppSumoSignUp = getPathName(path) === 'appsumo/signup/'
  const queryParams = getQueryObjFromURL();
  const appsumoCode = queryParams.code


  const {
    isFormLoading,
    contactNumber,
    signUpData,
    callingCode,
    OTPType,
    parentOrigin,
  } = useSignUpStore();
  const { signupVersion } = useSignupPageSetup();
  const cookies = parseCookies();
  const isUCECountry = isUceCountryCode(cookies?.countryCode);

  const { signInData } = useSignInStore();

  const handleOTP = (e: string) => {
    setOtp({ errorMsg: "", enteredOtp: e });
  };

  const actionOtpErrorAmplitudeEvent = () => {
    const { current_url, utm_params, screen_width, referrer } =
      AUTH_EVENTS.LOGIN_MODAL_INVALID_OTP_ERROR.PROPERTIES;

    new Amplitude().sendEvent({
      type: AUTH_EVENTS.LOGIN_MODAL_INVALID_OTP_ERROR.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [referrer]: getReferrer(),
      } as { [key: string]: string },
    });
  };

  const actionSignUpSuccessAmplitudeEvent = useCallback(
    (userId: string) => {
      const {
        current_url,
        screen_width,
        referrer,
        utm_params,
        name,
        email,
        phone_number,
        phone_country,
        signup_type,
        signup_version
      } = AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.PROPERTIES;
      const amplitudeProperties = {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [name]: signUpData?.schoolName,
        [email]: signUpData?.email,
        [phone_number]: contactNumber,
        [phone_country]: callingCode,
        [signup_type]: SIGNUP_TYPES.FULLPAGE,
        [signup_version]: signup_version
      };
      new Amplitude().sendEvent({
        type: AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.LABEL,
        properties: amplitudeProperties as { [key: string]: string },
        userId: userId,
      });
    },
    [callingCode, contactNumber, signUpData?.email, signUpData?.schoolName]
  );

  const actionSignUpSuccessGTMEvent = useCallback(
    (userId: string) => {
      const {
        current_url,
        screen_width,
        referrer,
        utm_params,
        name,
        email,
        phone_number,
        phone_country,
        signup_type,
      } = AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.PROPERTIES;

      const GTMProperties = {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [name]: signUpData?.schoolName,
        [email]: signUpData?.email,
        [phone_number]: contactNumber,
        [phone_country]: callingCode,
        [signup_type]: SIGNUP_TYPES.FULLPAGE,
        userId: userId,
      } as { [key: string]: string };

      sendGoogleTagEvent({
        eventName: AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.LABEL,
        gtmId: GTM_ID,
        isRootDataLayer: true,
        properties: GTMProperties,
      });
      sendGoogleTagEvent({
        eventName: GOOGLE_EVENTS.SIGNUP.INSTRUCTOR_SIGNUP_SUCCESS,
        gtmId: GTM_ID,
        isRootDataLayer: true,
        properties: GTMProperties,
      });
    },
    [callingCode, contactNumber, signUpData?.email, signUpData?.schoolName]
  );

  const clearFromLocalStorage = useCallback(() => {
    if (isRunningOnRootHost()) {
      clearFromLocal("utmFields");
      clearFromLocal("gref");
      clearFromLocal("QUERY_PARAMS");
    }
  }, []);

  const handleEmailSignup = useCallback(async () => {
    let token = "";

    const password = generator.generate({
      length: 10,
      numbers: true,
    });
    //@ts-ignore
    if (typeof grecaptcha !== "undefined") {
      //@ts-ignore
      token = await grecaptcha?.execute(RECAPTCHA_TOKEN, {
        action: "submit",
      });
    }

    const { email, schoolName } = signUpData || emptyObject;
    if (EmailValidator.validate(email) && (isAppSumoSignUp || isUCECountry || !!contactNumber) && !!schoolName) {
      const cookies = parseCookies();
      const countryCode = cookies?.countryCode;
      
      const isUCECountry = isUceCountryCode(countryCode);



      const signupValues = {
        email,
        phone: contactNumber ? `+${callingCode}${contactNumber}` : "",
        countryCode: callingCode,
        name: schoolName,
        recaptchaToken: token,
        signupType: SIGNUP_TYPE.NORMAL,
        password: password,
      } as CreatorSignupType;

      if(isUCECountry) {
        signupValues.destination = "home";
      }

      if(isAppSumoSignUp && appsumoCode) {
        signupValues.appsumoCode = appsumoCode as string
      }


      const emailResponse = await SignupController.emailSignup(signupValues);

      if (emailResponse.response && !emailResponse.old_graphy_user) {
        const { domain } = emailResponse;
        //@ts-ignore

        actionSignUpSuccessAmplitudeEvent(emailResponse.userId);
        if(emailResponse?.userId) {
          actionSignUpSuccessGTMEvent(emailResponse.userId)
        }
        spayeeAuthenticateSignUp({
          domain,
          email,
          password,
        });
        clearFromLocalStorage();
      }
      if (!emailResponse.response && emailResponse.message) {
        ToastAlert({
          toastType: TOAST_TYPES.ERROR,
          message: emailResponse?.message,
        });
      }
      if (emailResponse.old_graphy_user) {
        if (parentOrigin) {
          window.location.href = `${parentOrigin}/?signin&email=${email}`;
        } else {
          window.location.href = `/?signin&email=${email}`;
        }
      }
    }
  }, [
    actionSignUpSuccessAmplitudeEvent,
    callingCode,
    contactNumber,
    parentOrigin,
    signUpData,
  ]);

  const onSubmitOtp = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const { current_url, screen_width, referrer, utm_params, method, signup_type, signup_version } =
      OTPActionType === "signin" ? AUTH_EVENTS.SIGNIN_OTP_REQUESTED.PROPERTIES : AUTH_EVENTS.LOGIN_MODAL_LOGIN_TAPPED.PROPERTIES;
    new Amplitude().sendEvent({
      type: OTPActionType === "signin" ? AUTH_EVENTS.SIGNIN_OTP_REQUESTED.LABEL : AUTH_EVENTS.LOGIN_MODAL_LOGIN_TAPPED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [method]: OTPType === "phone" ? "phone otp" : "email otp",
        [signup_type]: SIGNUP_TYPES.FULLPAGE,
        [signup_version]: signup_version
      } as { [key: string]: string },
    });

    new Amplitude().sendEvent({
      type: OTPActionType === "signin" ? AUTH_EVENTS.SIGNIN_OTP_SUBMITTED.LABEL : AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.LABEL,
      properties: {
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.current_url]: getCurrentURL(),
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.screen_width]: getScreenWidth(),
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.referrer]: getReferrer(),
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.utm_params]: getUTMFieldsFromLocalStorage(),
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.source]: isAppSumoSignUp ? "appsumo" : isUCECountry ? "graphy-us" : "graphy-in",
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.type]: OTPType === "phone" ? "phone" : "email",
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.version]: signupVersion,
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.login_method]: SIGNIN_TYPE.OTP,
        [AUTH_EVENTS.SIGNUP_OTP_SUBMITTED.PROPERTIES.visitor_id]: localStorage.getItem('graphy_visitor_id') ?? "",
      } as { [key: string]: string },
    });

    if (OTPActionType === "signin") {
      try {
        if (otp?.enteredOtp && creatorDomain && signInData?.email) {
          const resp: any = await spayeeAuthenticate({
            email: signInData?.email,
            domain: creatorDomain,
            code: otp.enteredOtp,
          });

          if (resp?.response) {
            clearFromLocalStorage();
            const {
              current_url,
              utm_params,
              screen_width,
              referrer,
              login_method,
              email,
            } = AUTH_EVENTS.CREATOR_LOGIN_SUCCESS.PROPERTIES;
            otpAuthenticate({
              domain: creatorDomain,
              emailId: signInData?.email,
              password: otp.enteredOtp,
            });

            new Amplitude().sendEvent({
              type: AUTH_EVENTS.CREATOR_LOGIN_SUCCESS.LABEL,
              properties: {
                [current_url]: getCurrentURL(),
                [screen_width]: getScreenWidth(),
                [utm_params]: getUTMFieldsFromLocalStorage(),
                [referrer]: getReferrer(),
                [login_method]: "otp",
                [email]: signInData?.email,
              } as { [key: string]: string },
            });
          } else {
            actionOtpErrorAmplitudeEvent();
            setOtp({ ...otp, errorMsg: resp?.message });
            setIsSubmitting(false); 
          }
        } else {
          actionOtpErrorAmplitudeEvent();
          setOtp({ ...otp, errorMsg: "OTP is not valid, please recheck " });
          setIsSubmitting(false);
        }
      } catch (error) {
        const errorMsg = error as string;
        const {
          current_url,
          utm_params,
          screen_width,
          referrer,
          reason,
          email,
          login_method,
        } = AUTH_EVENTS.CREATOR_LOGIN_FAILED.PROPERTIES;

        new Amplitude().sendEvent({
          type: AUTH_EVENTS.CREATOR_LOGIN_FAILED.LABEL,
          properties: {
            [current_url]: getCurrentURL(),
            [screen_width]: getScreenWidth(),
            [utm_params]: getUTMFieldsFromLocalStorage(),
            [referrer]: getReferrer(),
            [reason]: error,
            [email]: signInData?.email,
            [login_method]: SIGNIN_TYPE.OTP,
          } as { [key: string]: string },
        });

        setOtp({ enteredOtp: "", errorMsg });
        setIsSubmitting(false);
      }
    } else {
      if (otp?.enteredOtp && signUpData?.email && (isAppSumoSignUp || isUCECountry || contactNumber)) {
        const otpAuthenticateValues = {
          email: signUpData?.email || "",
          otp: otp.enteredOtp,
          phone: contactNumber ? `+${callingCode}${contactNumber}` : "",
          type: OTPType,
          source: isAppSumoSignUp ? "appsumo" : isUCECountry ? "graphy-us" : undefined
        };

        try {
          const otpVerified = await otpAuthenticateOnSignUp(otpAuthenticateValues);

          if (otpVerified.response) {
            handleEmailSignup();
          } else {
            actionOtpErrorAmplitudeEvent();
            setOtp({ ...otp, errorMsg: otpVerified.message || "OTP verification failed" });
            setIsSubmitting(false); 
          }
        } catch (error) {
          actionOtpErrorAmplitudeEvent();
          const errorResponse = error as OTPVerificationResponse;
          setOtp({ ...otp, errorMsg: errorResponse.message || "OTP verification failed" });
          setIsSubmitting(false); 
        }
      } else {
        actionOtpErrorAmplitudeEvent();
        setOtp({ ...otp, errorMsg: "OTP is not valid, please recheck " });
        setIsSubmitting(false);
      }
    }
  };
  return (
    <form
      className="flex w-full flex-col items-center gap-[20px]"
      onSubmit={onSubmitOtp}
    >
      <div className="flex w-full flex-col gap-[20px]">
        <OtpInput
          value={otp.enteredOtp}
          onChange={handleOTP}
          numInputs={5}
          renderSeparator={<span className="mx-2"></span>}
          shouldAutoFocus={true}
          containerStyle="w-full justify-between"
          inputStyle={`h-[48px] !w-full min-w-0 max-w-[65px] rounded-[12px] border py-[12px] px-[16px] text-center outline-none focus:border-grey-4 ${otp?.errorMsg ? "border-errorMid" : "border-[#D4D7D7]"
            }`}
          renderInput={(props) => <input {...props} />}
        />
        {otp?.errorMsg && (
          <span className="text-center text-[14px] text-errorMid">{otp?.errorMsg}</span>
        )}
      </div>

      <button
        className="flex h-[48px] w-full max-w-[450px] items-center justify-center rounded-[100px] bg-[#4E5DE0] px-[24px] py-[12px] font-inter text-[15px] font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:cursor-not-allowed disabled:opacity-60"
        id="otp-verify-button"
        disabled={isFormLoading || otp.enteredOtp.length < 5}
      >
        <Div className={`text-center font-semibold text-white ${isFormLoading ? "cursor-not-allowed" : "hover:cursor-pointer"}`}>
          {isFormLoading ? (
            <Loader />
          ) : OTPActionType === "signin" ? (
            signupCopies.ctas.SIGN_IN
          ) : (
            signupCopies.ctas.CONTINUE ?? "Continue"
          )}
        </Div>
      </button>
      {OTPActionType === "signup" && (
        <p className="text-center font-inter text-[14px] font-medium leading-[20px] tracking-[-0.005em] text-[#878C90]">
          Didn&apos;t receive?{" "}
          {resendOtp && resendOtp > 0 ? (
            <span className="text-[#5766F2]">{`${signupCopies.ctas.RESEND_IN} ${resendOtp}s`}</span>
          ) : (
            <span className="cursor-pointer text-[#5766F2]" onClick={onResend}>
              {signupCopies.ctas.RESEND}
            </span>
          )}
        </p>
      )}
    </form>
  );
};